import styled from '@emotion/styled'
import { Button } from 'app/components/Common/Button'
import { FadeInOutAnimation } from 'app/components/Common/FadeInOutAnimation'
import { Image } from 'app/components/Common/Image'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { useVocabularyData } from 'app/utils/vocabulary'
import React, { memo } from 'react'
import * as Icon from 'react-feather'
import LazyLoad from 'react-lazyload'

export interface Props {
  benefits?: string[]
  IBEID?: number
  image?: { [key: string]: any }
  languageCode: string
  languagePrefix: string
  title?: string
}

export const Benefits = memo(function Benefits({
  benefits,
  IBEID,
  image,
  languageCode,
  languagePrefix,
  title,
}: Props) {
  return (
    <Container>
      <FadeInOutAnimation>
        <Wrapper>
          {image ? (
            <LazyLoad>
              <Image className="benefits-image" {...image} />
            </LazyLoad>
          ) : null}
          <TextWrapper dial={5} row space="between">
            {title ? <Title>{title}</Title> : null}
            {benefits ? (
              <List>
                {benefits.map((item, index) => (
                  <ListItem key={index}>
                    <Icon.Check />
                    {item}
                  </ListItem>
                ))}
              </List>
            ) : null}
            {IBEID ? (
              <CTA
                externalURL={`https://www.simplebooking.it/ibe/hotelbooking/search?hid=${IBEID}&lang=${languagePrefix.toLocaleUpperCase()}`}
                label={useVocabularyData('book-now', languageCode)}
                rel="noopener"
                target="_blank"
              />
            ) : null}
          </TextWrapper>
        </Wrapper>
      </FadeInOutAnimation>
    </Container>
  )
})

const Container = styled.section`
  margin: 7.5rem auto 0;

  @media (max-width: 767px) {
    margin-top: 5.25rem;
  }
`

const Wrapper = styled.div`
  padding: 3rem;
  position: relative;

  .benefits-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
  }

  @media (max-width: 1023px) {
    padding: 1.5rem;
  }
`

const TextWrapper = styled(FlexBox)`
  background: ${({ theme }) => theme.colors.variants.neutralLight4};
  padding: 5.625rem 7.639vw 8rem;

  @media (max-width: 1023px) {
    padding: 3.75rem 1.5rem 5.625rem;
  }

  @media (max-width: 767px) {
    display: block;
    padding: 2.25rem 1.5rem 0;
  }
`

const Title = styled.h3`
  width: 50%;
  max-width: 21.875rem;
  color: ${({ theme }) => theme.colors.variants.neutralDark2};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 1.875rem;
  line-height: 2.5rem;
  padding-right: 3rem;

  @media (max-width: 1023px) {
    max-width: none;
  }

  @media (max-width: 767px) {
    width: 100%;
    padding-right: 0;
  }
`

const List = styled.ul`
  display: flex;
  flex-wrap: wrap;
  width: 50%;

  @media (max-width: 767px) {
    display: block;
    width: 100%;
    margin-top: 2.25rem;
    margin-left: 1.5rem;
  }
`

const ListItem = styled.li`
  width: 50%;
  color: ${({ theme }) => theme.colors.variants.neutralDark3};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 0.875rem;
  line-height: 1.5rem;
  margin-top: 1.3125rem;
  padding: 0 1.25rem;
  position: relative;
  &:first-of-type,
  &:nth-of-type(2) {
    margin-top: 0;
  }
  svg {
    height: 20px;
    stroke: ${({ theme }) => theme.colors.variants.primaryDark1};
    position: absolute;
    top: 2px;
    left: -20px;
  }

  @media (max-width: 767px) {
    width: 100%;
    padding-right: 0;
    &:nth-of-type(2) {
      margin-top: 1.3125rem;
    }
    svg {
      height: 18px;
      top: 0;
    }
  }
`

const CTA = styled(Button)`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);

  @media (max-width: 767px) {
    margin-top: 2.25rem;
    position: relative;
    left: auto;
    transform: none;
  }
`
